import type {
  PropertyPreviewRootObserver,
  PropertyPreviewVisibilityRegistration,
  PropertyPreviewVisibilityRegistry,
} from '../types/property-preview.type';

const PRELOAD_MARGIN = '120px';
const SCROLL_OVERFLOW_VALUES = new Set(['auto', 'scroll', 'overlay']);

const scrollRoots = (element: HTMLElement): (Element | null)[] => {
  const roots: (Element | null)[] = [null];
  let ancestor = element.parentElement;
  while (ancestor) {
    const style = window.getComputedStyle(ancestor);
    if (
      SCROLL_OVERFLOW_VALUES.has(style.overflowX) ||
      SCROLL_OVERFLOW_VALUES.has(style.overflowY) ||
      SCROLL_OVERFLOW_VALUES.has(style.overflow)
    ) {
      roots.push(ancestor);
    }
    ancestor = ancestor.parentElement;
  }
  return roots;
};

/**
 * Shares observers for the viewport and actual board/column/list scroll roots.
 * A card is visible only after every relevant horizontal/vertical root agrees.
 * @returns A disposable registry with no per-card scroll event listeners.
 */
export const createPropertyPreviewVisibilityRegistry =
  (): PropertyPreviewVisibilityRegistry => {
    const registrations = new Map<
      HTMLElement,
      PropertyPreviewVisibilityRegistration
    >();
    const roots = new Map<Element | null, PropertyPreviewRootObserver>();

    const rootObserver = (
      root: Element | null
    ): PropertyPreviewRootObserver => {
      const existing = roots.get(root);
      if (existing) return existing;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!(entry.target instanceof HTMLElement)) continue;
            const registration = registrations.get(entry.target);
            if (!registration?.intersections.has(root)) continue;
            registration.intersections.set(root, entry.isIntersecting);
            const visible = [...registration.intersections.values()].every(
              Boolean
            );
            if (registration.visible !== visible) {
              registration.visible = visible;
              registration.onVisible(visible);
            }
          }
        },
        { root, rootMargin: PRELOAD_MARGIN, threshold: 0 }
      );
      const shared = { observer, elements: new Set<HTMLElement>() };
      roots.set(root, shared);
      return shared;
    };

    const register = (
      element: HTMLElement,
      onVisible: (visible: boolean) => void
    ): (() => void) => {
      onVisible(false);
      // Browsers supporting the app support IO. Absence never marks every mounted card visible.
      if (typeof IntersectionObserver === 'undefined') return () => undefined;

      const clippingRoots = scrollRoots(element);
      const registration: PropertyPreviewVisibilityRegistration = {
        intersections: new Map(clippingRoots.map((root) => [root, false])),
        onVisible,
        visible: false,
      };
      registrations.set(element, registration);
      for (const root of clippingRoots) {
        const shared = rootObserver(root);
        shared.elements.add(element);
        shared.observer.observe(element);
      }

      return () => {
        registrations.delete(element);
        registration.onVisible(false);
        for (const root of clippingRoots) {
          const shared = roots.get(root);
          if (!shared) continue;
          shared.observer.unobserve(element);
          shared.elements.delete(element);
          if (shared.elements.size === 0) {
            shared.observer.disconnect();
            roots.delete(root);
          }
        }
      };
    };

    const dispose = (): void => {
      for (const shared of roots.values()) shared.observer.disconnect();
      for (const registration of registrations.values()) {
        registration.onVisible(false);
      }
      registrations.clear();
      roots.clear();
    };

    return { register, dispose };
  };
